import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { FdeEditorDialog } from '@/frontend/features/Fde/FdeEditorSurface'
import { SelectControl, TextControl } from '@/frontend/features/Fde/FormControls'
import type { FdeCopy } from '@/frontend/features/Fde/types'
import { fdeRoutes } from '@/frontend/features/Fde/routes'

export default function DocumentUploadDialog({ open, onOpenChange, copy, projectId }: {
  open: boolean
  onOpenChange: (open: boolean) => void
  copy: FdeCopy
  projectId?: string | null
}) {
  const form = useForm<{ document_kind: string; occurred_at: string; directory: string; files: File[] }>({
    document_kind: 'transcricao',
    occurred_at: '',
    directory: 'transcricoes',
    files: [],
  })

  const submit = () => {
    form.transform((data) => ({ document: data }))
    form.post(`${fdeRoutes.documents}${projectId ? `?project_id=${projectId}` : ''}`, {
      forceFormData: true,
      preserveScroll: true,
      onSuccess: () => { form.reset(); onOpenChange(false) },
    })
  }

  return (
    <FdeEditorDialog
      open={open}
      onOpenChange={onOpenChange}
      title={copy.workspace.upload_document}
      closeLabel={copy.common.close}
      size="standard"
      footer={<><Button variant="outline" onClick={() => onOpenChange(false)}>{copy.common.cancel}</Button><Button disabled={form.processing || form.data.files.length === 0 || !form.data.directory.trim()} onClick={submit}>{copy.workspace.upload_document}</Button></>}
    >
        <div className="grid gap-5 sm:grid-cols-2">
          <label className="grid gap-2 text-sm font-medium text-foreground">
            {copy.forms.file}
            <input
              type="file"
              multiple
              required
              onChange={(event) => form.setData('files', Array.from(event.target.files ?? []))}
              className="block w-full rounded-lg border border-input bg-background px-3 py-2 text-sm file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm file:font-medium"
            />
            {form.data.files.length > 0 && <span className="text-xs font-normal text-muted-foreground">
              {copy.material.selected_files.replace('%{count}', String(form.data.files.length))}
            </span>}
          </label>
          <SelectControl
            label={copy.forms.document_kind}
            value={form.data.document_kind}
            onChange={(value) => {
              const defaultDirectories: Record<string, string> = { transcricao: 'transcricoes', documento: 'documentos', observacao: 'observacoes' }
              form.setData((current) => ({ ...current, document_kind: value, directory: defaultDirectories[value] ?? current.directory }))
            }}
            options={[
              { value: 'transcricao', label: copy.workspace.document_transcript },
              { value: 'documento', label: copy.workspace.document_file },
              { value: 'observacao', label: copy.workspace.document_observation },
            ]}
          />
          <TextControl label={copy.material.destination_directory} value={form.data.directory} onChange={(event) => form.setData('directory', event.target.value)} required />
          <TextControl label={copy.forms.occurred_at} type="datetime-local" value={form.data.occurred_at} onChange={(event) => form.setData('occurred_at', event.target.value)} />
          <p className="text-xs text-muted-foreground sm:col-span-2">{copy.material.destination_directory_hint}</p>
        </div>
    </FdeEditorDialog>
  )
}
